import { z } from 'zod';
import {
  PCMP_BOOK_PRICE_MAX,
  PCMP_PROJECT_CODE_MAX_LENGTH,
  PCMP_PROJECT_DESCRIPTION_MAX_LENGTH,
  PCMP_PROJECT_NAME_MAX_LENGTH,
  PCMP_PROJECT_SL_NO_MAX,
} from '../constants/pcmpProject.constants';
import { pcmpPriceKey } from '../utils/pcmpPriceGrid';

const BOOK_PRICE_RE = /^\d+(\.\d{1,2})?$/;
const WHOLE_NUMBER_RE = /^\d+$/;

/**
 * The project form, mirroring the API rules (legacy ValidateModel). The grid
 * is the chosen categories × chosen classes; `prices` holds the text of each
 * cell keyed by pcmpPriceKey, and only the cells of that grid are checked.
 */
export const pcmpProjectFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Name is required')
      .max(PCMP_PROJECT_NAME_MAX_LENGTH, `At most ${PCMP_PROJECT_NAME_MAX_LENGTH} characters`),
    code: z
      .string()
      .trim()
      .min(1, 'Code is required')
      .max(PCMP_PROJECT_CODE_MAX_LENGTH, `At most ${PCMP_PROJECT_CODE_MAX_LENGTH} characters`),
    description: z
      .string()
      .trim()
      .max(
        PCMP_PROJECT_DESCRIPTION_MAX_LENGTH,
        `At most ${PCMP_PROJECT_DESCRIPTION_MAX_LENGTH} characters`,
      ),
    companyId: z.number().int().optional(),
    bookEditionNo: z.number().int().optional(),
    activeFrom: z.string().min(1, 'Active From is required'),
    activeTo: z.string().min(1, 'Active To is required'),
    slNo: z
      .string()
      .trim()
      .refine(
        (value) =>
          value === '' || (WHOLE_NUMBER_RE.test(value) && Number(value) <= PCMP_PROJECT_SL_NO_MAX),
        'Enter a whole number',
      ),
    isActive: z.boolean(),
    isWebVisible: z.boolean(),
    isMobileVisible: z.boolean(),
    categoryIds: z.array(z.number()).min(1, 'Add at least one category'),
    classIds: z.array(z.number()).min(1, 'Pick at least one class'),
    prices: z.record(z.string(), z.string()),
  })
  .superRefine((values, ctx) => {
    if (values.companyId === undefined) {
      ctx.addIssue({ code: 'custom', path: ['companyId'], message: 'Company is required' });
    }
    if (values.bookEditionNo === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['bookEditionNo'],
        message: 'Book edition is required',
      });
    }
    if (values.activeFrom && values.activeTo && values.activeTo < values.activeFrom) {
      ctx.addIssue({
        code: 'custom',
        path: ['activeTo'],
        message: 'Active To must be on or after Active From',
      });
    }
    if (!values.isWebVisible && !values.isMobileVisible) {
      ctx.addIssue({
        code: 'custom',
        path: ['isMobileVisible'],
        message: 'Show the project on web, mobile or both',
      });
    }

    for (const categoryId of values.categoryIds) {
      for (const classId of values.classIds) {
        const key = pcmpPriceKey(categoryId, classId);
        const text = (values.prices[key] ?? '').trim();
        const message =
          text === ''
            ? 'Required'
            : !BOOK_PRICE_RE.test(text) || Number(text) > PCMP_BOOK_PRICE_MAX
              ? 'Invalid price'
              : null;
        if (message) ctx.addIssue({ code: 'custom', path: ['prices', key], message });
      }
    }
  });

export type PcmpProjectFormValues = z.infer<typeof pcmpProjectFormSchema>;
